import * as React from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Icon } from 'uxp/components';

/**
 * Icon adapter over uxp's universal <Icon>.
 *
 * uxp <Icon> supports FontAwesome AND Phosphor (auto-detected from the icon prop)
 * and — since the _icon.scss fix — sizes glyphs by font-size/em without ballooning
 * on load. But <Icon> only treats a PREFIXED string ("fas trash" / "phb house") or
 * a tuple as an icon; a bare name ("trash") is rendered as text. react-fontawesome
 * (what this codebase used before) defaulted a bare name to the `fas` prefix, and
 * many call sites rely on that. So normalize a bare FA name to ['fas', name] and
 * leave everything else (prefixed FA/phosphor, tuples, URLs, emoji/text) for <Icon>
 * to parse. This keeps every migrated `<FontAwesomeIcon>` site rendering as before
 * while enabling Phosphor wherever a "ph* name" string/tuple is passed.
 */
function normalizeIcon(icon: IconProp | string): IconProp | string {
    if (typeof icon !== 'string') return icon; // tuple / ReactElement — leave as-is
    const s = icon.trim();
    if (!s) return icon;
    if (/^(fa[bdlrs]|ph[rthlbfd])[\s:]/.test(s)) return icon;      // already-prefixed FA/phosphor
    if (/^(https?:|data:|\/)/.test(s) || s.includes('.')) return icon; // URL / path / image
    if (s.includes(' ')) return icon;                               // multi-word text glyph
    if (/^[a-zA-Z0-9-]+$/.test(s)) return ['fas', s] as IconProp;   // bare FA name → default fas
    return icon;                                                    // emoji / other → text
}

interface IFAIconProps {
    icon: IconProp | string;
    className?: string;
    style?: React.CSSProperties;
    color?: string;
    size?: string | number;
    spin?: boolean;
}

export const FAIcon: React.FunctionComponent<IFAIconProps> = ({ icon, className, style, color, size, spin }) => {
    return <Icon icon={normalizeIcon(icon)} className={className} style={style} color={color} size={size} spin={spin} />;
};
